import { Capacitor, registerPlugin } from '@capacitor/core';
import * as Tone from 'tone';
import multiNotePlayer from './multiNotePlayer';

/**
 * AudioSessionGuard - Bridges the native iOS AudioSessionGuardPlugin
 * to the JavaScript audio engine.
 *
 * On iOS (Capacitor), it listens for native audio session events
 * (interruptions, route changes, foreground return) and forces
 * the WKWebView AudioContext to actually resume.
 *
 * On web, it uses visibilitychange as a best-effort fallback.
 */

const AudioSessionGuard = Capacitor.isNativePlatform()
  ? registerPlugin('AudioSessionGuard')
  : null;

let initialized = false;
let recoveryInProgress = false;

/**
 * Force-resume the WKWebView AudioContext after iOS reclaims the audio session.
 * This is the fix for "0 sound after switching to YouTube/Spotify/etc."
 *
 * The native plugin calls setActive(true) on AVAudioSession BEFORE this runs,
 * so by the time we resume the AudioContext, the hardware audio route is ready.
 */
async function recoverAudioContext(reason) {
  if (recoveryInProgress) return;
  recoveryInProgress = true;

  try {
    // Step 1: Force Tone.js context to resume
    // This is necessary because Tone.start() checks its own internal state
    // and may think everything is fine when WKWebView's context is actually dead
    const context = Tone.getContext();
    if (context && context.rawContext) {
      const rawContext = context.rawContext;

      // If the context is suspended, resume it
      if (rawContext.state === 'suspended') {
        await rawContext.resume();
      }

      // Even if state says "running", force a resume anyway
      // because WKWebView lies about AudioContext state after interruptions
      try {
        await rawContext.resume();
      } catch (_) {
        // Ignore - best effort
      }
    }

    // Step 2: Re-run Tone.start() to ensure Tone.js internal state is synced
    try {
      await Tone.start();
    } catch (_) {
      // Ignore
    }

    // Step 3: Reset the gesture unlock flag so next user tap re-unlocks
    // This is critical because iOS may require a new user gesture after interruption
    multiNotePlayer.gestureUnlocked = false;
    multiNotePlayer.setupGestureUnlock();
  } catch (error) {
    console.error('[AudioSessionGuard] Recovery failed:', error);
  } finally {
    recoveryInProgress = false;
  }
}

/**
 * Initialize the audio session guard.
 * Call this once after the audio engine is initialized.
 */
export async function initAudioSessionGuard() {
  if (initialized) return;
  initialized = true;

  if (Capacitor.isNativePlatform() && AudioSessionGuard) {
    // Initialize the native plugin
    try {
      await AudioSessionGuard.initialize();
    } catch (error) {
      console.error('[AudioSessionGuard] Failed to initialize native plugin:', error);
    }

    AudioSessionGuard.addListener('audioSessionRestored', (data) => {
      recoverAudioContext(data.reason || 'native_restored');
    });

    AudioSessionGuard.addListener('audioInterrupted', () => {});
  } else {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        recoverAudioContext('visibility_change');
      }
    });
  }
}

export default {
  initAudioSessionGuard,
  recoverAudioContext
};
